import { clients } from 'lib/https';
import { ConfigurationSettings } from '/models/configuration-settings';

export interface OrganizationData {
  itemId: string;
  isActive: boolean;
  createdDate: string;
  lastUpdatedDate: string;
  createdBy: string;
  lastUpdatedBy: string;
  name: string;
  email: string;
  code: string;
  address: string;
  phoneNumber: string;
  websiteUrl: string;
}

export interface CreateOrganizationRequest {
  name: string;
  email: string;
  code: string;
  address: string;
  phoneNumber: string;
  websiteUrl: string;
}

export interface OrganizationResponse {
  data: OrganizationData[];
  totalCount: number;
}

export interface OrganizationFilters {
  name?: string;
  email?: string;
  code?: string;
  isActive?: boolean;
  createdDateFrom?: Date;
  createdDateTo?: Date;
}

export interface OrganizationApiFilter {
  email: string;
  name: string;
  active: boolean;
  inActive: boolean | null;
}

export interface GetOrganizationsPayload {
  page: number;
  pageSize: number;
  filters?: OrganizationFilters;
}

export interface UpdateOrganizationStatusRequest {
  itemId: string;
  isActive: boolean;
}

/**
 * Fetches organizations with pagination and filtering support.
 *
 * @param {GetOrganizationsPayload} payload - The payload containing pagination and filter parameters.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<OrganizationResponse>} The response containing organizations data and total count.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const organizations = await getOrganizations(payload, config);
 */
export const getOrganizations = async (
  payload: GetOrganizationsPayload,
  config: ConfigurationSettings
): Promise<OrganizationResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.organizationGets}`;
  
  const requestPayload = {
    page: payload.page,
    pageSize: payload.pageSize,
    filter: {
      email: payload.filters?.email || "",
      name: payload.filters?.name || "",
      active: payload.filters?.isActive === true,
      inActive: payload.filters?.isActive === false ? true : null
    }
  };
  
  const res = await clients.post<OrganizationResponse>(
    url,
    config.blocksKey,
    JSON.stringify(requestPayload)
  );
  
  return res;
};

/**
 * Fetches a single organization by its ID.
 *
 * @param {string} id - The ID of the organization to fetch.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<OrganizationData>} The organization data.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const organization = await getOrganization(id, config);
 */
export const getOrganization = async (
  id: string,
  config: ConfigurationSettings
): Promise<OrganizationData> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.organizationGet}`;
  
  const payload = { itemId: id };
  
  const res = await clients.post<{ data: OrganizationData[] }>(
    url,
    config.blocksKey,
    JSON.stringify(payload)
  );
  
  if (!res.data || res.data.length === 0) {
    throw new Error(`Organization with id ${id} not found`);
  }
  
  return res.data[0];
};

/**
 * Creates a new organization with the provided data.
 *
 * @param {CreateOrganizationRequest} data - The organization data to create.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<{ itemId: string; errors: unknown; isSuccess: boolean }>} The response containing the itemId, any errors, and the success status.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await createOrganization(organizationData, config);
 */
export const createOrganization = async (
  data: CreateOrganizationRequest,
  config: ConfigurationSettings
): Promise<{ itemId: string; errors: unknown; isSuccess: boolean }> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.organizationSave}`;
  
  return clients.post<{
    itemId: string;
    errors: unknown;
    isSuccess: boolean;
  }>(url, config.blocksKey, JSON.stringify(data));
};

/**
 * Updates an existing organization with the provided data.
 *
 * @param {string} id - The ID of the organization to update.
 * @param {Partial<CreateOrganizationRequest>} data - The organization data to update.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<{ itemId: string; errors: unknown; isSuccess: boolean }>} The response containing the itemId, any errors, and the success status.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateOrganization(id, organizationData, config);
 */
export const updateOrganization = async (
  id: string,
  data: Partial<CreateOrganizationRequest>,
  config: ConfigurationSettings
): Promise<{ itemId: string; errors: unknown; isSuccess: boolean }> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.organizationUpdate}`;
  
  const payload = {
    itemId: id,
    ...data
  };
  
  return clients.post<{
    itemId: string;
    errors: unknown;
    isSuccess: boolean;
  }>(url, config.blocksKey, JSON.stringify(payload));
};

/**
 * Updates the status (active/inactive) of an organization.
 *
 * @param {UpdateOrganizationStatusRequest} data - The organization status update data.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<{ itemId: string; errors: unknown; isSuccess: boolean }>} The response containing the itemId, any errors, and the success status.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await updateOrganizationStatus({ itemId: "123", isActive: false }, config);
 */
export const updateOrganizationStatus = async (
  data: UpdateOrganizationStatusRequest,
  config: ConfigurationSettings
): Promise<{ itemId: string; errors: unknown; isSuccess: boolean }> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.bebrave.organizationUpdateStatus}`;
  
  const payload = {
    ItemId: data.itemId,
    IsActive: data.isActive
  };
  
  return clients.post<{
    itemId: string;
    errors: unknown;
    isSuccess: boolean;
  }>(url, config.blocksKey, JSON.stringify(payload));
}; 